import React, { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Icon from "../../../components/AppIcon";
import {
  Code,
  Server,
  Shield,
  Database,
  Briefcase,
  Palette,
  Headphones,
  Sparkles,
  Grid3x3,
  ChevronLeft,
  ChevronRight
} from "lucide-react";

const FilterOptions = ({ categories, selectedCategory, onCategoryChange }) => {
  const scrollContainerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  // Check scroll position to show/hide arrows
  const checkScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 10);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    // Initial check after component mounts
    setTimeout(checkScroll, 100);
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [categories]);

  // Smooth scroll function
  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300;
      const newScrollLeft = scrollContainerRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);

      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="w-full">
      {/* Compact Header - Side by Side */}
      <div className="flex items-center justify-between mb-4">
        {/* Left: Header with Icon */}
        <motion.div
          className="flex items-center gap-3"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center shadow-lg flex-shrink-0">
            <Grid3x3 className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900 leading-tight">Browse Services</h2>
            <p className="text-xs text-slate-600">Filter by category</p>
          </div>
        </motion.div>

        {/* Right: Active Category Indicator */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="hidden sm:flex items-center gap-2 text-sm text-slate-600 bg-blue-50 px-4 py-2 rounded-xl border border-blue-100"
        >
          <div className="w-2 h-2 rounded-full bg-blue-600 animate-pulse" />
          <span className="whitespace-nowrap">
            Viewing: <span className="font-semibold text-slate-900">
              {categories.find(c => c.id === selectedCategory)?.name || 'All Services'}
            </span>
          </span>
        </motion.div>
      </div>

      {/* Scrollable Category Container */}
      <div className="relative">
        {/* Left Scroll Arrow */}
        <AnimatePresence>
          {showLeftArrow && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              onClick={() => scroll('left')}
              className="absolute left-1 top-0 bottom-0 my-auto z-30 w-9 h-9 rounded-full bg-white shadow-xl border-2 border-blue-200 flex items-center justify-center text-blue-600 hover:bg-blue-50 hover:border-blue-400 transition-all duration-300"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Scroll left"
            >
              <ChevronLeft className="w-5 h-5" strokeWidth={3} />
            </motion.button>
          )}
        </AnimatePresence>

        {/* Right Scroll Arrow */}
        <AnimatePresence>
          {showRightArrow && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              onClick={() => scroll('right')}
              className="absolute right-1 top-0 bottom-0 my-auto z-30 w-9 h-9 rounded-full bg-white shadow-xl border-2 border-blue-200 flex items-center justify-center text-blue-600 hover:bg-blue-50 hover:border-blue-400 transition-all duration-300"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Scroll right"
            >
              <ChevronRight className="w-5 h-5" strokeWidth={3} />
            </motion.button>
          )}
        </AnimatePresence>

        {/* Left Gradient Fade */}
        <div
          className={`absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-white to-transparent z-20 pointer-events-none transition-opacity duration-300 ${showLeftArrow ? 'opacity-100' : 'opacity-0'
            }`}
        />

        {/* Right Gradient Fade */}
        <div
          className={`absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-white to-transparent z-20 pointer-events-none transition-opacity duration-300 ${showRightArrow ? 'opacity-100' : 'opacity-0'
            }`}
        />

        {/* Scrollable Category Pills */}
        <motion.div
          ref={scrollContainerRef}
          onScroll={checkScroll}
          className="flex gap-3 overflow-x-auto py-2 scroll-smooth items-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch',
            paddingLeft: '2px',
            paddingRight: '2px'
          }}
        >
          {categories.map((category, index) => {
            const isSelected = selectedCategory === category.id;
            const CategoryIcon = getCategoryIconComponent(category.id);

            return (
              <motion.button
                key={category.id}
                onClick={() => onCategoryChange(category.id)}
                className={`relative flex-shrink-0 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 flex items-center gap-2 shadow-sm hover:shadow-lg overflow-hidden ${isSelected
                  ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg"
                  : "bg-white text-slate-700 border-2 border-slate-200 hover:border-blue-300 hover:bg-blue-50"
                  }`}
                aria-pressed={isSelected}
                whileHover={{ scale: 1.03, y: -2 }}
                whileTap={{ scale: 0.97 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                {/* Animated Background on Hover */}
                {!isSelected && (
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-blue-50 to-purple-50"
                    initial={{ x: "-100%" }}
                    whileHover={{ x: 0 }}
                    transition={{ duration: 0.3 }}
                  />
                )}

                {/* Icon and Text */}
                <span className="relative z-10 flex items-center gap-2 whitespace-nowrap">
                  {category.id !== "all" && CategoryIcon && (
                    <CategoryIcon
                      className={`w-4 h-4 ${isSelected ? 'text-white' : 'text-slate-600'}`}
                      strokeWidth={2.5}
                    />
                  )}
                  {category.id === "all" && (
                    <Sparkles
                      className={`w-4 h-4 ${isSelected ? 'text-yellow-300' : 'text-slate-600'}`}
                      strokeWidth={2.5}
                    />
                  )}
                  {category.name}
                </span>

                {/* Selection Indicator - Bottom Bar */}
                <AnimatePresence>
                  {isSelected && (
                    <motion.div
                      className="absolute bottom-0 left-0 right-0 h-1 bg-yellow-300 rounded-b-xl"
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: 1 }}
                      exit={{ scaleX: 0 }}
                      transition={{ duration: 0.3 }}
                    />
                  )}
                </AnimatePresence>

                {/* Shine Effect on Active */}
                {isSelected && (
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                    initial={{ x: "-100%" }}
                    animate={{ x: "100%" }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut",
                      repeatDelay: 1
                    }}
                  />
                )}
              </motion.button>
            );
          })}
        </motion.div>
      </div>

      {/* Mobile Active Category Indicator */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="mt-3 flex sm:hidden items-center gap-2 text-sm text-slate-600"
      >
        <div className="w-2 h-2 rounded-full bg-blue-600 animate-pulse" />
        <span>
          Viewing: <span className="font-semibold text-slate-900">
            {categories.find(c => c.id === selectedCategory)?.name || 'All Services'}
          </span>
        </span>
      </motion.div>

      {/* Hide Scrollbar */}
      <style jsx>{`
        div::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

// Helper function to get icon component for each category
const getCategoryIconComponent = (categoryId) => {
  const iconMap = {
    development: Code,
    infrastructure: Server,
    security: Shield,
    data: Database,
    consulting: Briefcase,
    design: Palette,
    support: Headphones
  };

  return iconMap[categoryId] || null;
};

export default FilterOptions;
